import React from 'react';
import { Paper, Tabs, Tab, Box, Typography, SxProps, Theme, useMediaQuery, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../../components/auth/AuthProvider';

interface FeatureNavTabsProps {
  activeFeature?: string;
  onChangeFeature?: (feature: string) => void;
  sx?: SxProps<Theme>;
}

const FeatureNavTabs: React.FC<FeatureNavTabsProps> = ({ activeFeature, onChangeFeature, sx }) => {
  const location = useLocation();
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.between('sm', 'md'));
  const { isAuthenticated } = useAuth();
  const pathname = location.pathname || '/trade-codes';
  // Determine current tab by route; when on /about (or other non-data routes), show no selection
  const current =
    pathname.startsWith('/systems')
      ? 'fullValueChain'
      : pathname.startsWith('/downstream-potential')
      ? 'downstreamPotential'
      : pathname.startsWith('/technology-types')
      ? 'hsCodeValueChain'
      : pathname.startsWith('/companies')
      ? 'companies'
      : pathname.startsWith('/trade-codes')
      ? 'hsGroupedHSCode'
      : false;

  return (
    <Paper elevation={1} sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, ...(sx || {}) }}>
      {/* Header hint */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, pt: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.2 }}>
            Choose view by aggregation level
          </Typography>
          <Tooltip
            title="Navigate the same value chains at different levels of resolution: from Trade Codes (most aggregated) to Systems and their types (least aggregated)."
            placement={isXs ? 'bottom' : 'right'}
            enterTouchDelay={0}
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 260,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
              },
            }}
            PopperProps={{
              modifiers: [
                { name: 'preventOverflow', options: { padding: 8, boundary: 'viewport' } },
              ],
            }}
          >
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
      </Box>
      {isXs ? (
        <Box sx={{ p: 1.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 28px',
              alignItems: 'stretch',
              gap: 1
            }}
          >
            <Tabs
              orientation="vertical"
              value={current}
              onChange={() => {}}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  minHeight: 56,
                  textAlign: 'left',
                  borderRadius: 1,
                  transition: 'background-color 120ms ease, color 120ms ease',
                  cursor: 'pointer'
                },
                '& .MuiTab-root:hover': {
                  backgroundColor: (t) => t.palette.action.hover,
                  color: 'primary.main'
                },
                '& .MuiTab-root:hover .MuiTypography-root': {
                  color: 'primary.main'
                }
              }}
            >
              <Tab
                value="hsGroupedHSCode"
                component={RouterLink}
                to="/trade-codes"
                label={(
                  <Box sx={{ lineHeight: 1.1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 400 }}>Trade Codes</Typography>
                    <Typography variant="caption" color="text.secondary">(Most aggregated / lowest resolution)</Typography>
                  </Box>
                )}
              />
              <Tab
                value="hsCodeValueChain"
                component={RouterLink}
                to="/technology-types"
                label={(
                  <Box sx={{ lineHeight: 1.1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 400 }}>Technology Types (system types)</Typography>
                    <Typography variant="caption" color="text.secondary">(Second most aggregated)</Typography>
                  </Box>
                )}
              />
              <Tab
                value="companies"
                component={RouterLink}
                to="/companies"
                disabled={!isAuthenticated}
                label={(
                  <Box sx={{ lineHeight: 1.1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 400 }}>
                      {`Companies${!isAuthenticated ? ' (login required)' : ''}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">(Second most aggregated)</Typography>
                  </Box>
                )}
              />
              <Tab
                value="downstreamPotential"
                component={RouterLink}
                to="/downstream-potential"
                disabled={!isAuthenticated}
                label={(
                  <Box sx={{ lineHeight: 1.1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 400 }}>
                      {`Downstream Potential${!isAuthenticated ? ' (login required)' : ''}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">(Second most aggregated)</Typography>
                  </Box>
                )}
              />
              <Tab
                value="fullValueChain"
                component={RouterLink}
                to="/systems"
                label={(
                  <Box sx={{ lineHeight: 1.1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 400 }}>Systems and their types</Typography>
                    <Typography variant="caption" color="text.secondary">Highest resolution (least aggregated)</Typography>
                  </Box>
                )}
              />
            </Tabs>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                <svg width="16" height="100%" viewBox="0 0 10 100" preserveAspectRatio="none" style={{ display: 'block' }}>
                  <defs>
                    <marker id="varrow" markerWidth="6" markerHeight="6" refX="5.2" refY="3" orient="auto">
                      <polygon points="0 0, 6 3, 0 6" fill="currentColor" />
                    </marker>
                  </defs>
                  <line x1="5" y1="0" x2="5" y2="100" stroke="currentColor" strokeWidth="0.8" marker-end="url(#varrow)" />
                </svg>
              </Box>
            </Box>
          </Box>
          <Box sx={{ pt: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              (resolution from lowest to highest)
            </Typography>
          </Box>
        </Box>
      ) : (
        <Tabs
          value={current}
          onChange={() => {}}
          variant={isSm ? 'scrollable' : 'fullWidth'}
          scrollButtons={isSm ? 'auto' : false}
          allowScrollButtonsMobile={isSm}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isSm ? '0.95rem' : '1rem',
              py: isSm ? 1.5 : 2,
              minWidth: isSm ? 140 : undefined,
              borderRadius: 1,
              transition: 'background-color 120ms ease, color 120ms ease',
              cursor: 'pointer'
            },
            '& .MuiTab-root:hover': {
              backgroundColor: (t) => t.palette.action.hover,
              color: 'primary.main'
            },
            '& .MuiTab-root:hover .MuiTypography-root': {
              color: 'primary.main'
            }
          }}
        >
          <Tab
            value="hsGroupedHSCode"
            component={RouterLink}
            to="/trade-codes"
            label={(
              <Tooltip title="Trade Codes: most aggregated (lowest resolution)" enterTouchDelay={0}>
                <Box sx={{ lineHeight: 1.1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>Trade Codes</Typography>
                  {!isSm && <Typography variant="caption" color="text.secondary">(Most aggregated / lowest resolution)</Typography>}
                </Box>
              </Tooltip>
            )}
          />
          <Tab
            value="hsCodeValueChain"
            component={RouterLink}
            to="/technology-types"
            label={(
              <Tooltip title="Technology Types: second most aggregated" enterTouchDelay={0}>
                <Box sx={{ lineHeight: 1.1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>Technology Types (system types)</Typography>
                  {!isSm && <Typography variant="caption" color="text.secondary">(Second most aggregated)</Typography>}
                </Box>
              </Tooltip>
            )}
          />
          <Tab
            value="companies"
            component={RouterLink}
            to="/companies"
            disabled={!isAuthenticated}
            label={(
              <Tooltip
                title={
                  isAuthenticated
                    ? 'Companies: second most aggregated (systems view of firms)'
                    : 'Login required to access Companies'
                }
                enterTouchDelay={0}
              >
                <Box sx={{ lineHeight: 1.1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>
                    {`Companies${!isAuthenticated ? ' (login required)' : ''}`}
                  </Typography>
                  {!isSm && <Typography variant="caption" color="text.secondary">(Second most aggregated)</Typography>}
                </Box>
              </Tooltip>
            )}
          />
          <Tab
            value="downstreamPotential"
            component={RouterLink}
            to="/downstream-potential"
            disabled={!isAuthenticated}
            label={(
              <Tooltip
                title={
                  isAuthenticated
                    ? 'Downstream Potential: second most aggregated'
                    : 'Login required to access Downstream Potential'
                }
                enterTouchDelay={0}
              >
                <Box sx={{ lineHeight: 1.1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>
                    {`Downstream Potential${!isAuthenticated ? ' (login required)' : ''}`}
                  </Typography>
                  {!isSm && <Typography variant="caption" color="text.secondary">(Second most aggregated)</Typography>}
                </Box>
              </Tooltip>
            )}
          />
          <Tab
            value="fullValueChain"
            component={RouterLink}
            to="/systems"
            label={
              <Tooltip title="Systems: highest resolution (least aggregated)" enterTouchDelay={0}>
                <Box sx={{ lineHeight: 1.1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>Systems and their types</Typography>
                  {!isSm && <Typography variant="caption" color="text.secondary">Highest resolution (least aggregated)</Typography>}
                </Box>
              </Tooltip>
            }
          />
        </Tabs>
      )}
      {/* Left-to-right resolution arrow (hidden on mobile) */}
      {!isXs && (
        <Box sx={{ px: 2, pt: 1, pb: 2, color: 'text.secondary' }}>
          <Box sx={{ width: '100%', height: 20 }}>
            <svg width="100%" height="20" viewBox="0 0 100 20" preserveAspectRatio="none" style={{ display: 'block' }}>
              <defs>
                <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="5.2" refY="3" orient="auto">
                  <polygon points="0 0, 6 3, 0 6" fill="currentColor" />
                </marker>
              </defs>
              <line x1="0" y1="10" x2="100" y2="10" stroke="currentColor" strokeWidth="0.8" marker-end="url(#arrowhead)" />
            </svg>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              (resolution from lowest to highest)
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default FeatureNavTabs;


